import React, { useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Input from "@mui/material/Input";
import Stack from "@mui/material/Stack";

import { DataGrid } from "@mui/x-data-grid";

import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";

export default function MobileGrid({ primaryColumns, ...gridProps }) {
    const [sortedRows, setSortedRows] = useState(null);
    const mobilePrimaryColumns = primaryColumns ? primaryColumns.map((columnName) => gridProps.columns.find((c) => c.field === columnName)) : gridProps.columns;
    const mobileSecondaryColumns = gridProps.columns.filter((c) => !mobilePrimaryColumns.includes(c));
    const mobileColumn = [
        {
            field: "",
            headerName: "",
            flex: 1,
            renderCell: (params) => (
                <MobileColumn
                    primaryColumns={mobilePrimaryColumns}
                    secondaryColumns={mobileSecondaryColumns}
                    params={params}
                    processRowUpdate={gridProps.processRowUpdate}
                />
            ),
            disableColumnMenu: true
        }
    ];

    useEffect(() => {
        if (!Array.isArray(gridProps.rows)) {
            return;
        }

        // Sort model is not present (or it has been cycled to null order).
        if (!Array.isArray(gridProps.sortModel) || gridProps.sortModel.length === 0) {
            setSortedRows(gridProps.rows);
            return;
        }

        const sortModel = gridProps.sortModel[0];
        const sortField = sortModel.field;
        const sortDirection = sortModel.sort;
        const rows = [...gridProps.rows];
        rows.sort((a, b) => {
            const aField = a[sortField];
            const bField = b[sortField];
            if (aField < bField) {
                return sortDirection === "asc" ? -1 : 1;
            }
            if (aField > bField) {
                return sortDirection === "asc" ? 1 : -1;
            }
            return 0;
        });
        setSortedRows(rows);
    }, [gridProps.rows, gridProps.sortModel]);

    // The primary columns must be loaded.
    if (mobilePrimaryColumns.some((column) => !column)) {
        return <></>;
    }

    return (
        <DataGrid
            {...gridProps}
            rows={sortedRows}
            columns={mobileColumn}
            columnHeaderHeight={1}
            getRowHeight={() => "auto"}
            disableVirtualization
            disableRowSelectionOnClick
            sortModel={undefined}
            onSortModelChange={() => {}}
            sx={{
                ...gridProps.sx,
                "& .MuiDataGrid-cell": { padding: 0 },
                "& .MuiDataGrid-cellCheckbox": { borderRight: "2px solid rgb(81, 81, 81)" }
            }}
        />
    );
}

export function MobileColumn({ primaryColumns, secondaryColumns, params, processRowUpdate }) {
    const [expanded, setExpanded] = useState(false);

    const displayHeader = (column) => {
        if (column.headerName) {
            return column.headerName;
        }

        let header = "";
        const words = column.field.split("_");
        for (let i = 0; i < words.length; i++) {
            header += words[i][0].toUpperCase() + words[i].substring(1) + (i == words.length - 1 ? "" : " ");
        }
        return header;
    };

    const displayColumn = (column) => {
        if (!column || !params) {
            return <Typography>None</Typography>;
        }

        // Handles the various renderCell's depending on component type.
        if ("renderCell" in column) {
            const element = formatElement(column.renderCell({ ...params, value: params.row[column.field], formattedValue: params.row[column.field] }));
            if (element) {
                return checkForSpecialSelection(column, element);
            }
        }

        // Handles the rendering of the action icon buttons.
        // For some odd reason, in almost all getAction definitions, we use the same key prop for each item?
        if ("getActions" in column) {
            return column.getActions(params).map((element, i) => {
                if (!React.isValidElement(element.props.icon)) return React.cloneElement(element, { key: i, sx: { ...element.props.sx, p: 0 } });
                const icon = React.cloneElement(element.props.icon, { sx: { fontSize: "22px" } });
                return React.cloneElement(element, { key: i, sx: { ...element.props.sx, p: 0 }, icon });
            });
        }

        // Default rendering of columns (plain text).
        let value = params.row[column.field];
        if ("valueGetter" in column) {
            value = column.valueGetter(value, params.row, column);
        }
        if ("valueFormatter" in column) {
            value = column.valueFormatter(value, params.row, column);
        }
        return checkForSpecialSelection(column, <Typography sx={{ overflowWrap: "break-word" }}>{value ? String(value) : "None"}</Typography>);
    };

    const formatElement = (element) => {
        // Renders any strings as plain text.
        if (!React.isValidElement(element)) {
            return element ? <Typography>{String(element)}</Typography> : null;
        }

        // Recursively formats any children elements.
        if (Array.isArray(element.props.children)) {
            return React.cloneElement(element, { children: React.Children.map(element.props.children, (child) => formatElement(child)) });
        }

        // Special rendering for Buttons.
        if (element.type.render?.name === "Button") {
            return React.cloneElement(element, { sx: { p: 0, fontSize: "16px", lineHeight: "0" } });
        }

        // Renders plain text elements (children is a string).
        if (element.props.children && !React.isValidElement(element.props.children)) {
            return <Typography sx={{ ...element.props.style, fontSize: "medium" }}>{element.props.children}</Typography>;
        }

        // Default rendering of elements. Remove padding for IconButton/Checkbox formatting.
        return React.cloneElement(element, { sx: { p: 0 } });
    };

    const checkForSpecialSelection = (column, element) => {
        if (!("type" in column) || column.type !== "singleSelect" || !("valueOptions" in column)) {
            return element;
        }

        const options = typeof column.valueOptions === "function" ? column.valueOptions(params) : column.valueOptions;
        return (
            <FormControl variant="standard" size="small">
                <Select
                    value={params.row[column.field]}
                    onChange={(e) => processRowUpdate({ ...params.row, [column.field]: e.target.value }, params.row)}
                    renderValue={() => element}
                    input={<Input disableUnderline />}
                    sx={{ maxHeight: "20px", mt: "4px", "& .MuiSelect-icon": { mt: "-3px" } }}
                >
                    {options.map((option, i) => (
                        <MenuItem key={i} value={typeof option === "string" ? option : option.value}>
                            {typeof option === "string" ? option : option.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        );
    };

    return (
        <Stack>
            <Stack direction="row" sx={{ p: 2, alignItems: "center" }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {primaryColumns.map((column, i) => (
                        <Stack key={i} direction={"row"} spacing={1} data-testid={column.field}>
                            <Typography color="text.secondary" fontWeight="bold" sx={{ overflowWrap: "break-word" }}>
                                {displayHeader(column)}:
                            </Typography>
                            {displayColumn(column)}
                        </Stack>
                    ))}
                </Box>
                {secondaryColumns.length >= 1 && (
                    <IconButton onClick={() => setExpanded(!expanded)} data-testid="expandRow">
                        {expanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                )}
            </Stack>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <hr style={{ border: "2px solid rgb(81, 81, 81)", margin: 0 }} />
                <Box sx={{ p: 2 }}>
                    {secondaryColumns.map((column, i) => (
                        <Stack key={i} direction="row" spacing={1}>
                            <Typography color="text.secondary" fontWeight="bold" sx={{ overflowWrap: "break-word" }}>
                                {displayHeader(column)}:
                            </Typography>
                            {displayColumn(column)}
                        </Stack>
                    ))}
                </Box>
            </Collapse>
        </Stack>
    );
}
